import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import styles from './style.module.scss';
import { ActionOptionIcon, EditIcon, ProjectNameIcon, StatusCheckIcon, DetailIcon, EyeIcon } from 'assets';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import GenericTable from 'shared/components/common/table/table';
import DashboardWrapper from 'shared/components/common/dashboard_wrapper/DashboardWrapper';
import Heading from 'shared/components/common/heading/Heading';
import Status from 'shared/components/common/status/Status';
import RoleCheckBox from 'shared/components/common/role_check_box/RoleCheckBox';
import LiveStatus from 'shared/components/common/status/LiveStatus';

import TaskView from 'shared/modal/task_view/TaskView';
import ManageLiveStatus from 'shared/modal/manage_live_status/ManageLiveStatus';
import { GetAllLiveStatuses } from 'shared/services/live_status_services/LiveStatusServices';
import { GetAllTaskQuery, UpdateTaskMutation } from 'shared/services/task_service/TaskService';
import { taskPriority } from './Constant';
import Select from 'react-select';
import OverlayLoading from 'shared/screen_loader/overlay_loader/OverlayLoading';
import ImageViewer from './ImageViewer';
import { isRestrictActions } from 'shared/utils/Helper';
import { screenActionEnum, screenEnum } from 'shared/utils/Enum';
import SearchBar from 'shared/components/common/searchbar/SearchBar';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import ExcelExport from 'shared/components/common/ExcelExport/ExcelExport';
import moment from 'moment';
import ModalImage from 'shared/components/common/custom_image_lightbox/ModalImage';
import { toastMessage } from 'shared/components/toast/Toast';

import { GetAllRoughListingQuery } from 'shared/services/rough_listing_service/RoughListingService';
import { routeConstant } from 'shared/routes/RouteConstant';

export default function ManageTask() {
    const {
        theme: { theme }
    } = useSelector((state) => state.root);
    const navigate = useNavigate();
    const [milestoneOption, setMileStoneOption] = useState(false);
    const [activeInx, setActiveInx] = useState(-1);
    const [selectedMilestone, setSelectedMilestone] = useState('');
    const [isEdit, setIsEdit] = useState(false);
    const [openWidget, setOpenWidget] = useState(false);
    const dropDownRef = useRef(null);
    const [viewModalShow, setViewModalShow] = useState(false);
    const [viewTask, setViewTask] = useState(null);
    const [manageLiveStatusModalShow, setManageLiveStatusModalShow] = useState(false);
    const toggleEditUser = () => {
        setIsEdit(!isEdit);
    };
    const [searchMenu, setsearchMenu] = useState('');
    const [clicksearch, setClickSearch] = useState('');
    const [searchResults, setSearchResults] = useState([]);

    const [expandedGroups, setExpandedGroups] = useState({});
    const expandedGroupsRef = useRef(expandedGroups);
    expandedGroupsRef.current = expandedGroups;

    const getBaseSku = (sku) => {
        if (!sku) return '';
        const match = sku.match(/^([a-zA-Z]*\d+)[a-zA-Z]*$/);
        if (match) {
            return match[1];
        }
        return sku;
    };

    const { data: roughListingList } = GetAllRoughListingQuery();

    const rlSkuMap = React.useMemo(() => {
        const map = {};
        (roughListingList || []).forEach(rl => {
            (rl.order || []).forEach(item => {
                if (item.sku) {
                    map[item.sku] = rl;
                }
            });
        });
        return map;
    }, [roughListingList]);

    const tableData = React.useMemo(() => {
        const normalGroups = {};
        const normalGroupOrder = [];
        const excelTasks = [];

        // 1. Deduplicate tasks by SKU (or _id if no SKU)
        // If multiple tasks have the exact same SKU, prioritize the one with images or latest update
        const skuMap = new Map();
        (searchResults || []).forEach(task => {
            const rawSku = (task.task_inventory?.sku || task.task_sku || '').trim().toLowerCase();
            const key = rawSku || task._id;
            if (!key) return;

            if (!skuMap.has(key)) {
                skuMap.set(key, task);
            } else {
                const existing = skuMap.get(key);
                const existingImgs = (existing.designed_images?.length || 0) + (existing.rectifired_images?.length || 0) + (existing.raw_images?.length || 0);
                const currentImgs = (task.designed_images?.length || 0) + (task.rectifired_images?.length || 0) + (task.raw_images?.length || 0);
                if (currentImgs > existingImgs) {
                    skuMap.set(key, task);
                } else if (currentImgs === existingImgs) {
                    const existingDate = new Date(existing.updated_at || existing.created_at || 0).getTime();
                    const currentDate = new Date(task.updated_at || task.created_at || 0).getTime();
                    if (currentDate > existingDate) {
                        skuMap.set(key, task);
                    }
                }
            }
        });
        const uniqueResults = Array.from(skuMap.values());
        
        uniqueResults.forEach(task => {
            const isExcelTask = task.created_at && new Date(task.created_at).getFullYear() <= 2020;
            
            if (isExcelTask) {
                // Excel bulk upload tasks are direct single flat rows
                excelTasks.push({
                    ...task,
                    isParentRow: false,
                    hasChildren: false,
                    isChildRow: false,
                    baseSku: task.task_inventory?.sku || task.task_sku || ''
                });
            } else {
                // Normal tasks (both Rough Listing and PO / Inventory): group by baseSku tree
                const sku = task.task_inventory?.sku || task.task_sku || '';
                const base = getBaseSku(sku) || sku || 'Other';
                if (!normalGroups[base]) {
                    normalGroups[base] = [];
                    normalGroupOrder.push(base);
                }
                normalGroups[base].push(task);
            }
        });

        const result = [];

        // 2. Add normal tasks grouped by base SKU tree
        normalGroupOrder.forEach(base => {
            const groupTasks = normalGroups[base];
            groupTasks.sort((a, b) => {
                const skuA = (a.task_inventory?.sku || a.task_sku || '').toLowerCase();
                const skuB = (b.task_inventory?.sku || b.task_sku || '').toLowerCase();
                const baseLower = base.toLowerCase();
                if (skuA === baseLower) return -1;
                if (skuB === baseLower) return 1;
                return skuA.localeCompare(skuB);
            });

            // Check if there is an exact task for the base SKU
            const exactBaseTask = groupTasks.find(t => (t.task_inventory?.sku || t.task_sku || '').toLowerCase() === base.toLowerCase());
            
            // Representative task for the parent row (prefer exactBaseTask, or task with images, or first task)
            const representativeTask = exactBaseTask || 
                groupTasks.find(t => ((t.designed_images?.length || 0) + (t.rectifired_images?.length || 0) + (t.raw_images?.length || 0)) > 0) || 
                groupTasks[0];

            // If representative task has no images, aggregate images from the group for the parent preview
            const hasAnyImages = (representativeTask.designed_images?.length || 0) + (representativeTask.rectifired_images?.length || 0) + (representativeTask.raw_images?.length || 0);
            let parentTaskImages = {};
            if (!hasAnyImages) {
                const taskWithImages = groupTasks.find(t => ((t.designed_images?.length || 0) + (t.rectifired_images?.length || 0) + (t.raw_images?.length || 0)) > 0);
                if (taskWithImages) {
                    parentTaskImages = {
                        designed_images: taskWithImages.designed_images || [],
                        rectifired_images: taskWithImages.rectifired_images || [],
                        raw_images: taskWithImages.raw_images || []
                    };
                }
            }

            const hasChildren = exactBaseTask ? groupTasks.length > 1 : groupTasks.length > 0;

            result.push({
                ...representativeTask,
                ...parentTaskImages,
                isParentRow: true,
                hasChildren: hasChildren,
                baseSku: base,
                childCount: exactBaseTask ? groupTasks.length - 1 : groupTasks.length
            });

            if (expandedGroups[base]) {
                groupTasks.forEach(child => {
                    // If exactBaseTask exists, it is already represented as parent row, so skip it from children
                    if (exactBaseTask && child._id === exactBaseTask._id) {
                        return;
                    }
                    result.push({
                        ...child,
                        isChildRow: true,
                        isParentRow: false,
                        hasChildren: false,
                        baseSku: base
                    });
                });
            }
        });

        // 3. Add Excel tasks at the bottom as flat individual rows
        excelTasks.forEach(task => {
            result.push(task);
        });

        return result;
    }, [searchResults, expandedGroups, roughListingList, rlSkuMap]);

    const obj = {};
    if (clicksearch) {
        obj['search'] = clicksearch;
    }
    const allTaskQueryParams = new URLSearchParams(obj).toString();
    const { isLoading, data: dataTask, isError: isErr, error: err } = GetAllTaskQuery(allTaskQueryParams);
    const { mutate: updateTaskMutate } = UpdateTaskMutation();
    
    const { data: rawLiveStatuses } = GetAllLiveStatuses();
    const liveStatusOptions = React.useMemo(() => {
        if (rawLiveStatuses && rawLiveStatuses.length > 0) {
            return rawLiveStatuses.map(item => ({
                label: item.name,
                value: item.name,
                color: item.color
            }));
        }
        return [];
    }, [rawLiveStatuses]);

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            backgroundColor: theme === 'dark' ? '#1e293b' : '#ffffff',
            borderColor: theme === 'dark' ? '#334155' : '#e2e8f0',
            color: theme === 'dark' ? '#f8fafc' : '#0f172a',
            minHeight: '34px',
            height: '34px',
            fontSize: '13px',
            boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
            '&:hover': {
                borderColor: '#94a3b8'
            }
        }),
        valueContainer: (provided) => ({
            ...provided,
            height: '34px',
            padding: '0 8px'
        }),
        input: (provided) => ({
            ...provided,
            margin: '0px',
            color: theme === 'dark' ? '#f8fafc' : '#0f172a'
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            height: '34px'
        }),
        singleValue: (provided, state) => ({
            ...provided,
            color: state.data.color ? state.data.color : (theme === 'dark' ? '#f8fafc' : '#0f172a'),
            fontWeight: '600'
        }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: theme === 'dark' ? '#1e293b' : '#ffffff',
            border: `1px solid ${theme === 'dark' ? '#334155' : '#e2e8f0'}`,
            zIndex: 9999
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected 
                ? '#3b82f6' 
                : state.isFocused 
                    ? (theme === 'dark' ? '#334155' : '#f1f5f9') 
                    : 'transparent',
            color: state.isSelected 
                ? '#ffffff' 
                : (state.data.color || (theme === 'dark' ? '#f8fafc' : '#0f172a')),
            fontSize: '13px',
            fontWeight: '500',
            cursor: 'pointer'
        })
    };

    const handleUpdateTaskField = (task, field, value) => {
        setSearchResults((prev) =>
            prev.map((t) => (t._id === task._id ? { ...t, [field]: value } : t))
        );
        const payload = {
            id: task._id,
            body: {
                task_inventory: task.task_inventory?._id,
                task_product: task.task_product?._id,
                liveStatus: task.liveStatus || 'Not Yet',
                priority: task.priority || 'Low',
                comments: task.comments || '',
                rectifired_images: task.rectifired_images || [],
                designed_images: task.designed_images || [],
                [field]: value
            }
        };
        updateTaskMutate(payload);
    };


    const closeViewIT = () => {
        setViewModalShow(!viewModalShow);
    };

    const columns = React.useMemo(
        () => [
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: '100%' }}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        SKU
                    </div>
                ),
                accessor: 'sku',
                width: 180,
                minWidth: 150,
                Cell: ({ row }) => {
                    const isParent = row.original?.isParentRow;
                    const hasChildren = row.original?.hasChildren;
                    const isChild = row.original?.isChildRow;
                    const baseSku = row.original?.baseSku;
                    const isExpanded = expandedGroupsRef.current[baseSku];

                    return (
                        <div 
                            style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                paddingLeft: '0px',
                                cursor: 'pointer', 
                                textAlign: 'left', 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis', 
                                whiteSpace: 'nowrap', 
                                width: '100%',
                                fontWeight: isParent ? 'bold' : 'normal'
                            }}
                            onClick={() => {
                                const sku = isParent ? baseSku : (row.original?.task_inventory?.sku || row.original?.task_sku);
                                if (sku) {
                                    navigator.clipboard.writeText(sku);
                                    toastMessage('success', 'SKU Copied!');
                                }
                            }}
                        >
                            {isParent && hasChildren && (
                                <span 
                                    style={{ 
                                        marginRight: '8px', 
                                        cursor: 'pointer', 
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '18px',
                                        height: '18px',
                                        borderRadius: '4px',
                                        background: isExpanded ? '#4a86f7' : 'rgba(0,0,0,0.05)',
                                        color: isExpanded ? 'white' : '#666',
                                        fontSize: '10px',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedGroups(prev => ({
                                            ...prev,
                                            [baseSku]: !prev[baseSku]
                                        }));
                                    }}
                                >
                                    {isExpanded ? '▼' : '▶'}
                                </span>
                            )}
                            {isParent && !hasChildren && (
                                <span style={{ width: '26px' }} />
                            )}
                            {isChild && (
                                <span style={{ marginLeft: '26px', marginRight: '12px', color: '#b0b0b0', fontWeight: 'bold', fontSize: '14px' }}>↳</span>
                            )}
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>
                                {isParent ? baseSku : (row.original?.task_inventory?.sku || row.original?.task_sku || 'N/A')}
                            </span>
                        </div>
                    );
                }
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Image
                    </div>
                ),
                accessor: 'task_image',
                width: 75,
                minWidth: 65,
                disableSortBy: true,
                Cell: ({ row }) => {
                    const task = row.original;
                    const images = [
                        ...(task?.designed_images || []),
                        ...(task?.rectifired_images || []),
                        ...(task?.raw_images || []),
                        ...(task?.task_subcategory?.image ? [task.task_subcategory.image] : []),
                        ...(task?.image ? [task.image] : []),
                        ...(task?.task_inventory?.image ? [task.task_inventory.image] : [])
                    ].filter(Boolean);

                    if (images.length === 0) return <div className="text-center text-muted" style={{ width: '100%' }}>-</div>;

                    const firstImg = images[0];
                    return (
                        <div className="d-flex align-items-center justify-content-center" style={{ width: '100%' }}>
                            <div style={{ position: 'relative', width: '36px', height: '36px' }}>
                                <ModalImage
                                    small={firstImg}
                                    large={firstImg}
                                    imageArray={images}
                                    alt={task?.name || 'task image'}
                                    style={{
                                        width: '36px',
                                        height: '36px',
                                        borderRadius: '6px',
                                        objectFit: 'cover',
                                        border: '1px solid #e2e8f0'
                                    }}
                                />
                                {images.length > 1 && (
                                    <span
                                        style={{
                                            position: 'absolute',
                                            bottom: '-3px',
                                            right: '-3px',
                                            background: '#3b82f6',
                                            color: 'white',
                                            fontSize: '9px',
                                            fontWeight: '700',
                                            borderRadius: '8px',
                                            padding: '1px 3px',
                                            lineHeight: '1',
                                            pointerEvents: 'none'
                                        }}
                                    >
                                        +{images.length - 1}
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                }
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: '100%' }}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Product
                    </div>
                ),
                accessor: 'productCombined',
                Cell: ({ row }) => {
                    const task = row.original;
                    const isParent = task?.isParentRow;
                    const taskSku = task?.task_sku || task?.sku || task?.baseSku;
                    const brand = task?.task_compatiableBrand?.name || '';
                    let model = task?.task_product?.name || task?.task_product?.model || '';
                    if (!model || model.toLowerCase().includes('rough listing')) {
                        if (task?.name && !task.name.toLowerCase().includes('rough listing')) {
                            model = task.name;
                        } else {
                            const rl = rlSkuMap[taskSku];
                            const rlItem = rl?.order?.find(it => it.sku === taskSku);
                            if (rlItem?.product?.name || rlItem?.product?.model) {
                                model = rlItem.product.name || rlItem.product.model;
                            }
                        }
                    }
                    if (model && model.toLowerCase().includes('rough listing')) {
                        model = '';
                    }
                    const subcat = task?.task_subcategory?.name || '';
                    const cat = task?.task_category?.name || '';
                    const color = task?.task_inventory?.color || task?.task_color || task?.color || '';
                    
                    if (isParent) {
                        const isScreenProtector = cat.toLowerCase().includes('screen protector');
                        let combinedText = '';
                        if (isScreenProtector) {
                            combinedText = `${brand} ${model} ${cat}`.replace(/\s+/g, ' ').trim();
                        } else {
                            combinedText = `${brand} ${model} ${subcat} ${cat}`.replace(/\s+/g, ' ').trim();
                        }
                        return (
                            <div
                                style={{ cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left', width: '100%' }}
                                title="Click to copy all product details"
                                onClick={() => {
                                    if (combinedText) {
                                        navigator.clipboard.writeText(combinedText);
                                        toastMessage('success', 'Product Details Copied!');
                                    }
                                }}
                            >
                                {combinedText || '-'}
                            </div>
                        );
                    } else {
                        const isScreenProtector = cat.toLowerCase().includes('screen protector');
                        const fullCombinedText = `${brand} ${model} ${subcat} ${cat} ${color}`.replace(/\s+/g, ' ').trim();
                        
                        return (
                            <div
                                style={{ cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left', width: '100%' }}
                                title="Click to copy all product details"
                                onClick={() => {
                                    if (fullCombinedText) {
                                        navigator.clipboard.writeText(fullCombinedText);
                                        toastMessage('success', 'Product Details Copied!');
                                    }
                                }}
                            >
                                {brand && `${brand} `}
                                {model && `${model} `}
                                {isScreenProtector ? (
                                    <>
                                        {subcat && <span style={{ color: '#4a86f7', fontWeight: 'bold' }}>{subcat} </span>}
                                        {cat && `${cat} `}
                                    </>
                                ) : (
                                    <>
                                        {subcat && `${subcat} `}
                                        {cat && `${cat} `}
                                    </>
                                )}
                                {color && <span style={{ color: '#4a86f7', fontWeight: 'bold' }}>{color}</span>}
                            </div>
                        );
                    }
                }
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: '100%' }}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Brand
                    </div>
                ),
                accessor: 'brand',
                Cell: ({ row }) => <div style={{ textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>{row.original?.task_brand?.name}</div>
            },

            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: '100%' }}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Date
                    </div>
                ),
                accessor: 'date',
                Cell: ({ row }) => {
                    const taskDate = row?.original?.created_at || row?.original?.createdAt || row?.original?.date || row?.original?.updated_at;
                    return (
                        <div className={styles.truncate} style={{ textAlign: 'left', width: '100%' }}>
                            {taskDate ? moment(taskDate).format('DD, MMM YYYY') : '-'}
                        </div>
                    );
                }
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: '100%' }}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Submitted By
                    </div>
                ),
                accessor: 'submitted_by',
                width: 150,
                minWidth: 130,
                Cell: ({ row }) => {
                    const task = row.original;
                    const taskSku = task.task_sku || task.sku || task.baseSku;
                    const rl = rlSkuMap[taskSku];

                    if (!rl) return <div className="text-muted" style={{ textAlign: 'left' }}>-</div>;

                    const submittedByName = rl.submitted_by?.name;
                    const issueDate = rl.created_at || rl.date;

                    return (
                        <div style={{ lineHeight: '1.3', textAlign: 'left' }}>
                            <div style={{ fontWeight: '500' }}>{submittedByName || '-'}</div>
                            {issueDate && (
                                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                                    {moment(issueDate).format('DD, MMM YYYY')}
                                </div>
                            )}
                        </div>
                    );
                }
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: '100%' }}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Approved By
                    </div>
                ),
                accessor: 'approved_by',
                width: 150,
                minWidth: 130,
                Cell: ({ row }) => {
                    const task = row.original;
                    const taskSku = task.task_sku || task.sku || task.baseSku;
                    const rl = rlSkuMap[taskSku];

                    if (!rl) return <div className="text-muted" style={{ textAlign: 'left' }}>-</div>;

                    const approvedByName = rl.approved_by?.name;
                    const approvalDate = rl.approved_at || rl.updated_at || rl.created_at;

                    return (
                        <div style={{ lineHeight: '1.3', textAlign: 'left' }}>
                            <div style={{ fontWeight: '500' }}>{approvedByName || '-'}</div>
                            {approvalDate && (
                                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                                    {moment(approvalDate).format('DD, MMM YYYY')}
                                </div>
                            )}
                        </div>
                    );
                }
            },

            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Priority
                    </div>
                ),
                accessor: 'priority',
                Cell: ({ row }) => (
                    <div style={{ minWidth: '130px' }}>
                        <Select
                            options={taskPriority}
                            value={taskPriority.find(opt => opt.value === row.original.priority) || {label: row.original.priority || 'Low', value: row.original.priority || 'Low'}}
                            onChange={(item) => {
                                handleUpdateTaskField(row.original, 'priority', item.value);
                            }}
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        />
                    </div>
                )
            },

            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <StatusCheckIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Task Status
                    </div>
                ),
                accessor: 'status',
                Cell: ({ row }) => (
                    <div>
                        <Status status={row.original.status} statusContainer={classNames(styles.status)} />
                    </div>
                )
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)} style={{ display: 'flex', alignItems: 'center' }}>
                        <StatusCheckIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Live Status
                        <i 
                            className="ms-2" 
                            style={{ fontStyle: 'normal', cursor: 'pointer', opacity: 0.8 }}
                            onClick={() => setManageLiveStatusModalShow(true)}
                            title="Manage Live Statuses"
                        >
                            ⚙️
                        </i>
                    </div>
                ),
                accessor: 'liveStatus',
                width: 220,
                minWidth: 180,
                disableSortBy: true,
                Cell: ({ row }) => {
                    const selectedVal = liveStatusOptions.find(opt => opt.value === row.original.liveStatus) || {label: row.original.liveStatus || 'Not Yet', value: row.original.liveStatus || 'Not Yet', color: '#34495e'};
                    return (
                        <div style={{ width: '100%', minWidth: '170px' }}>
                            <Select
                                options={liveStatusOptions}
                                value={selectedVal}
                                onChange={(item) => {
                                    handleUpdateTaskField(row.original, 'liveStatus', item.value);
                                }}
                                menuPortalTarget={document.body}
                                styles={{
                                    ...customSelectStyles,
                                    menuPortal: base => ({ ...base, zIndex: 9999 })
                                }}
                            />
                        </div>
                    );
                }
            },

            {
                Header: (
                    <div className={classNames(`${theme}FontColor`)}>
                        <ActionOptionIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Action
                    </div>
                ),
                accessor: 'action',
                disableSortBy: true,
                Cell: ({ row }) => {
                    const task = row.original;
                    const isParentGroup = task.isParentRow;
                    // A task is from rough listing if it has NO task_inventory
                    const isRoughListingTask = !task.task_inventory;
                    const taskSku = task.task_sku || task.sku || task.baseSku;

                    const handleEditRoughListingTask = (e) => {
                        e.stopPropagation();
                        if (!taskSku) {
                            toastMessage('error', 'Task SKU not found');
                            return;
                        }

                        // Find matching rough listing by SKU
                        const matchingRL = (roughListingList || []).find(rl =>
                            (rl.order || []).some(item => item.sku === taskSku)
                        );

                        if (!matchingRL) {
                            toastMessage('error', `Matching Rough Listing not found for SKU: ${taskSku}`);
                            return;
                        }

                        navigate(routeConstant.admin.roughListing.addrl.path, {
                            state: {
                                editData: matchingRL,
                                editSku: taskSku,
                                fromTask: true
                            }
                        });
                    };

                    return (
                        <div className="d-flex align-items-center gap-2">
                            <EyeIcon
                                cursor="pointer"
                                title="View Task Details"
                                onClick={() => {
                                    setViewTask(task);
                                    setViewModalShow(true);
                                }}
                            />
                            {!isParentGroup && isRoughListingTask && (
                                <EditIcon
                                    cursor="pointer"
                                    style={{ width: '17px', height: '17px' }}
                                    title="Edit Rough Listing Item & Sync Task"
                                    onClick={handleEditRoughListingTask}
                                />
                            )}
                        </div>
                    );
                }
            }
        ],

        [theme, milestoneOption, liveStatusOptions, customSelectStyles, roughListingList, rlSkuMap]
    );

    return (
        <DashboardWrapper>
            <div className={classNames(styles.UserContainer, 'text-start')}>
                <SearchBar
                    searchKey="name"
                    suggestionsSource={dataTask}
                    onSearchChange={setSearchResults} // ✅ REQUIRED!
                />                <div className={styles.Header}>
                    <Heading heading={'Task'} headingStyle={styles.Heading} />
                    <div className="d-flex">
                        <ExcelExport data={dataTask} fileName={'Task List'} />
                    </div>
                </div>
                <GenericTable columns={columns} data={tableData} TableWrapperStyle={classNames(styles.Table)} />
            </div>
            <TaskView show={viewModalShow} handleClose={closeViewIT} tasks={viewTask} isAdmin={true} />
            <ManageLiveStatus show={manageLiveStatusModalShow} handleClose={() => setManageLiveStatusModalShow(false)} />
            {isLoading && <OverlayLoading />}
        </DashboardWrapper>
    );
}
